import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useBlocker } from 'react-router-dom'

import DiscardChangesDialog from '@/components/DiscardChangesDialog'
import useGoBack from '@/hooks/useGoBack'

/**
 * Combines all "unsaved changes" guarding concerns:
 * - beforeunload prompt when closing/refreshing the tab
 * - in-app navigation interception (react-router-dom useBlocker)
 * - confirmation dialog rendering and handling
 *
 * Usage:
 * const { dialog, guardRef, requestBack } = useUnsavedChangesGuard(isDirty)
 * // render dialog somewhere in your component tree: {dialog}
 * // set guardRef.current = false before programmatic navigate after save
 * // call requestBack() from Cancel buttons
 */
export default function useUnsavedChangesGuard(isDirty: boolean): {
  dialog: ReactNode;
  guardRef: React.MutableRefObject<boolean>;
  requestBack: () => void;
} {
  const goBack = useGoBack()
  const guardRef = useRef(true)

  // showManualConfirm is only for the Cancel-button flow (requestBack),
  // which does NOT go through the router blocker.
  const [showManualConfirm, setShowManualConfirm] = useState(false)

  // Beforeunload: warn user when closing/refreshing the tab
  useEffect(() => {
    if (!isDirty) return

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault()
        // eslint-disable-next-line better-mutation/no-mutation
        e.returnValue = ''
        return ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [isDirty])

  // In-app navigation blocking using react-router-dom
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && guardRef.current && currentLocation.pathname !== nextLocation.pathname
  )

  // Dialog is visible when the blocker has intercepted a navigation
  // OR when the user pressed the Cancel button (manual flow).
  const isDialogOpen = blocker.state === 'blocked' || showManualConfirm

  // Dialog handlers
  const handleKeepEditing = useCallback(() => {
    if (blocker.state === 'blocked') {
      blocker.reset?.()
    }
    setShowManualConfirm(false)
  }, [blocker])

  const handleDiscard = useCallback(() => {
    guardRef.current = false
    if (blocker.state === 'blocked') {
      blocker.proceed?.()
    } else {
      goBack()
    }
    setShowManualConfirm(false)
  }, [blocker, goBack])

  // External helper to be used from Cancel button
  const requestBack = useCallback(() => {
    if (isDirty) {
      setShowManualConfirm(true)
    } else {
      goBack()
    }
  }, [goBack, isDirty])

  const dialog = useMemo<ReactNode>(() => (
    <DiscardChangesDialog
      isOpen={isDialogOpen}
      onCancel={handleDiscard}
      onAccept={handleKeepEditing}
    />
  ), [isDialogOpen, handleDiscard, handleKeepEditing])

  return { dialog, guardRef, requestBack }
}
